import Markdown from '@wandb/weave/common/components/Markdown';
import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import {fuzzyMatchWithMapping} from '@wandb/weave/common/util/fuzzyMatch';
import {Link} from '@wandb/weave/common/util/links';
import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {Icon} from '@wandb/weave/components/Icon';
import {isValidEmail} from '@wandb/weave/core';
import _ from 'lodash';
import qs from 'query-string';
import React, {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {
  Card,
  Container,
  Header,
  Input,
  type StrictInputProps,
} from 'semantic-ui-react';

import {ReactComponent as CoreWeaveMark} from '../assets/SVG/icon-coreweave-mark.svg';
import {ReactComponent as LinkedInIcon} from '../assets/SVG/icon-linkedin-social.svg';
import {ReactComponent as XIcon} from '../assets/SVG/icon-x-social.svg';
import {AUTHOR_TYPES} from '../components/FC/config';
import {CW_BLUE, CW_BLUE_HOVER} from '../components/FC/coreweaveColors';
import {FeaturedBanner} from '../components/FC/FeaturedBanner';
import {PostCard} from '../components/FC/PostCard';
import {TopicFilters} from '../components/FC/TopicFilters';
import type {AuthorType} from '../components/FC/types';
import {FCTopNav} from '../components/FCTopNav';
import ImageIconHeader from '../components/ImageIconHeader';
import {useHideNavOnPage} from '../components/NavContextProvider';
import {PageWithSidebar} from '../components/PageWithSidebar';
import {publicAssetUrl} from '../config';
import {
  useCreateNewsletterSubscriptionMutation,
  useNewsletterSubscriptionQuery,
} from '../generated/graphql';
import {GALLERY_PATH_SEGMENT} from '../routes/report/reportPaths';
import {isMountedInShell} from '../shellRemote';
import {usePrevious} from '../state/hooks';
import {PanelBankContextProvider} from '../state/panelbank/context';
import {useViewer} from '../state/viewer/hooks';
import docURLs from '../util/doc_urls';
import {setDocumentDescription, setDocumentTitle} from '../util/document';
import {propagateErrorsContext} from '../util/errors';
import {
  type Banner,
  type BizzaboEvent,
  DEFAULT_LANGUAGE,
  deriveTagPage,
  EMPTY_GALLERY_SPEC,
  type FCPage,
  formatGalleryTag,
  type GallerySpec,
  getLabelForFCPage,
  getSetActiveRepoLink,
  getSetActiveTagLink,
  getSetPageLink,
  isFCPage,
  isLanguage,
  type Language,
  MAIN_PAGE,
  projectUsages,
  type ReportIDWithTagIDs,
  type ReportMetadata,
  type RepoWithUsage,
  TAG_PAGES,
  type TagV2,
  useBizzaboEvents,
  useGallerySpec,
  useReportMetadata,
  useReportMetadataWithCache,
  useReportTags,
  useTagReportData,
} from '../util/gallery';
import {useDebounceState, useScrollIntoView} from '../util/hooks';
import {imgSrcWithDefault} from '../util/images';
import {parseReportParams} from '../util/report';
import * as URLs from '../util/urls';
import {InstrumentedLoader as Loader} from './../components/utility/InstrumentedLoader';
import * as S from './FC.styles';
// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
import {ReportPageContainer} from './ReportPage/ReportPageContainer';
import {ReportPageContextProvider} from './ReportPage/ReportPageContext';
import {Weave1PanelContextProvider} from './ReportPage/Weave1PanelContext';

type RouteParams = NormalParams | ReportPageParams | RepoParams | TopicParams;

type NormalParams = {
  page?: string;
  tag?: string;
};

type ReportPageParams = {
  entityName: string;
  projectName: string;
  reportNameAndID: string;
};

type RepoParams = {
  repoName: string;
};

type TopicParams = {
  topic: string;
};

function isReportPageParams(p: RouteParams): p is ReportPageParams {
  return `reportNameAndID` in p;
}

function isRepoParams(p: RouteParams): p is RepoParams {
  return `repoName` in p;
}

function isTopicParams(p: RouteParams): p is TopicParams {
  return `topic` in p;
}

type ReportParams = {
  id: string;
  entityName: string;
  projectName?: string;
  displayName: string;
};

export type FCContextState = Omit<
  GallerySpec,
  'categories' | 'tags' | 'repos'
> &
  Omit<FCReportsResult, 'updateReportMetadata'> &
  StateFromURL & {
    loading: boolean;
    loadingEvents: boolean;
    loadingNewsletterSubscription: boolean;
    currentPage: number;
    minPage: number;
    maxPage: number;
    activeReportParams: ReportParams | null;
    searchQuery: string;
    searchQueryDebounced: string;
    homeCategoryName: string;
    subscribeModalOpen: boolean;
    events: BizzaboEvent[];
    reposWithGitHubMetadata: RepoWithGitHubMetadata[];
    subscribedToNewsletter: boolean;
    reportIDSetByTagName: Map<string, Set<string>>;
    getSetCurrentPageLink: (
      p: number
    ) => Partial<ReturnType<typeof useLocation>>;
    reportBelongsToTag: (reportID: string, tagName: string) => boolean;
    activeAuthorType: AuthorType;
    banner: Banner;
  };

const FCContext = React.createContext<FCContextState>({
  ...EMPTY_GALLERY_SPEC,
  loading: false,
  loadingReports: false,
  loadingEvents: false,
  loadingNewsletterSubscription: false,
  page: MAIN_PAGE,
  reports: [],
  newReports: [],
  popularReports: [],
  currentPage: 1,
  minPage: 1,
  maxPage: 1,
  activeTag: ``,
  activeReportID: null,
  activeReportParams: null,
  activeRepo: null,
  activeTopic: null,
  language: DEFAULT_LANGUAGE,
  searchQuery: ``,
  searchQueryDebounced: ``,
  homeCategoryName: ``,
  subscribeModalOpen: false,
  events: [],
  reposWithGitHubMetadata: [],
  subscribedToNewsletter: false,
  reportIDSetByTagName: new Map(),
  getSetCurrentPageLink: () => ({}),
  reportBelongsToTag: () => false,
  activeAuthorType: 'All',
  banner: {text: '', url: '', visible: false},
});

export type FCContextUpdaters = {
  setPage: (page: FCPage) => void;
  setActiveReportID: (id: string) => void;
  setActiveRepo: (repo: string) => void;
  setSearchQuery: (query: string) => void;
  setSearchQueryLiveAndDebounced: (query: string) => void;
  setSubscribeModalOpen: (open: boolean) => void;
  updateReportMetadata: (
    reportID: string,
    updateFn: (r: ReportMetadata) => void
  ) => void;
  setActiveAuthorType: (type: AuthorType) => void;
};

const FCUpdaterContext = React.createContext<FCContextUpdaters>({
  setPage: () => {},
  setActiveReportID: () => {},
  setActiveRepo: () => {},
  setSearchQuery: () => {},
  setSearchQueryLiveAndDebounced: () => {},
  setSubscribeModalOpen: () => {},
  updateReportMetadata: () => {},
  setActiveAuthorType: () => {},
});

const FCContextProvider: React.FC<{children?: React.ReactNode}> = React.memo(
  ({children}) => {
    const history = useHistory();
    const viewer = useViewer();
    const {params} = useRouteMatch<RouteParams>();

    const {
      loading: loadingNewsletterSubscription,
      data: newsletterSubscriptionData,
    } = useNewsletterSubscriptionQuery({
      variables: {
        userName: viewer?.username ?? '',
      },
      skip: !viewer?.username,
    });
    const subscribedToNewsletter = useMemo(
      () => newsletterSubscriptionData?.user?.newsletterSubscription != null,
      [newsletterSubscriptionData]
    );

    const {loading, gallerySpec} = useGallerySpec();
    const {tagsV2, reportIDsWithTagV2IDs, repos, eventMetadatas} = gallerySpec;

    const projectUsageByProjectName = useMemo(
      () => new Map(projectUsages.map(u => [u.projectName, u])),
      []
    );
    const reposWithUsage: RepoWithUsage[] = useMemo(
      () =>
        repos
          .filter(r => !r.pending)
          .map(r => {
            const projectUsage = projectUsageByProjectName.get(r.name);
            return {...r, userCounts: projectUsage?.userCounts ?? []};
          }),
      [repos, projectUsageByProjectName]
    );
    const reposWithGitHubMetadata = useGitHubMetadata(reposWithUsage);

    const {loading: loadingEvents, events} = useBizzaboEvents(eventMetadatas);

    const homeCategoryName = useMemo(
      () => tagsV2.find(t => t.linkForm === '')?.name ?? MAIN_PAGE,
      [tagsV2]
    );

    const {reportIDSetByTagName, reportBelongsToTag} = useTagReportData();

    // Read state from URL
    const {
      page,
      activeTag,
      activeReportID,
      activeRepo,
      activeTopic,
      language,
      currentPage,
      activeAuthorType,
    } = useDeriveStateFromURL({
      params,
      reportIDsWithTagV2IDs,
      tags: tagsV2,
      homeCategoryName,
    });

    const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);

    const [
      searchQuery,
      searchQueryDebounced,
      setSearchQuery,
      setSearchQueryLiveAndDebounced,
    ] = useDebounceState('', 300);

    const {reportParams: activeReportParams} =
      useActiveReportParams(activeReportID);

    const {
      loadingReports,
      newReports,
      popularReports,
      reports: allReports,
      updateReportMetadata,
    } = useFCReports({
      reportIDsWithTagV2IDs,
      tagsV2,
      activeTag,
      language,
      searchQueryDebounced,
      reportBelongsToTag,
    });

    const getSetCurrentPageLink = useCallback(
      (p: number) => {
        const searchObj = qs.parse(history.location.search);

        if (p === 1) {
          delete searchObj.page;
        } else {
          searchObj.page = String(p);
        }

        return {
          pathname: history.location.pathname,
          search: qs.stringify(searchObj),
        };
      },
      [history]
    );
    const setCurrentPage = useCallback(
      (p: number) => {
        history.push(getSetCurrentPageLink(p));
      },
      [getSetCurrentPageLink, history]
    );

    const setPage = useCallback(
      (newPage: FCPage) => {
        if (newPage === MAIN_PAGE) {
          history.push(`/${GALLERY_PATH_SEGMENT}`);
          return;
        }
        const pathSegments = [GALLERY_PATH_SEGMENT, newPage];
        history.push(`/${pathSegments.join(`/`)}`);
      },
      [history]
    );
    const setActiveReportID = useCallback(
      (id: string) => {
        const pathSegments = isMountedInShell()
          ? [GALLERY_PATH_SEGMENT, id]
          : [GALLERY_PATH_SEGMENT, `blog`, `tag`, id];
        history.push(`/${pathSegments.join(`/`)}`);
      },
      [history]
    );
    const setActiveRepo = useCallback(
      (name: string) => {
        const pathSegments = [GALLERY_PATH_SEGMENT, `projects`, name];
        history.push(`/${pathSegments.join(`/`)}`);
      },
      [history]
    );

    const setActiveAuthorType = useCallback(
      (type: AuthorType) => {
        const searchObj = qs.parse(history.location.search);
        if (type === 'All') {
          delete searchObj.author;
        } else {
          searchObj.author = type;
        }
        // Reset page to 1 when changing filters
        delete searchObj.page;

        history.push({
          pathname: history.location.pathname,
          search: qs.stringify(searchObj),
        });
      },
      [history]
    );

    // Filter based on activeAuthorType
    const filterByAuthor = useCallback(
      (reportList: ReportMetadata[]) => {
        if (activeAuthorType === 'All') {
          return reportList;
        }
        return reportList.filter(report => {
          const authorLower = activeAuthorType.toLowerCase();
          return report.tags?.some(
            tag => tag.name.toLowerCase() === authorLower
          );
        });
      },
      [activeAuthorType]
    );

    const filteredAllReports = useMemo(
      () => filterByAuthor(allReports),
      [allReports, filterByAuthor]
    );
    const filteredNewReports = useMemo(
      () => filterByAuthor(newReports),
      [newReports, filterByAuthor]
    );
    const filteredPopularReports = useMemo(
      () => filterByAuthor(popularReports),
      [popularReports, filterByAuthor]
    );

    const {
      currentItems: reports,
      minPage,
      maxPage,
    } = usePagination({
      loadingItems: loadingReports,
      allItems: filteredAllReports,
      itemsPerPage: 21,
      currentPage,
      setCurrentPage,
    });

    const state = useMemo<FCContextState>(
      () => ({
        loading,
        loadingReports,
        loadingEvents,
        loadingNewsletterSubscription,
        ...gallerySpec,
        reports,
        newReports: filteredNewReports,
        popularReports: filteredPopularReports,
        currentPage,
        minPage,
        maxPage,
        homeCategoryName,
        page,
        activeTag,
        activeReportID,
        activeReportParams,
        activeRepo,
        activeTopic,
        language,
        events,
        reposWithGitHubMetadata,
        searchQuery,
        searchQueryDebounced,
        subscribeModalOpen,
        subscribedToNewsletter,
        reportIDSetByTagName,
        getSetCurrentPageLink,
        reportBelongsToTag,
        activeAuthorType,
        banner: gallerySpec.banner,
      }),
      [
        loading,
        loadingReports,
        loadingEvents,
        loadingNewsletterSubscription,
        gallerySpec,
        reports,
        filteredNewReports,
        filteredPopularReports,
        currentPage,
        minPage,
        maxPage,
        homeCategoryName,
        page,
        activeTag,
        activeReportID,
        activeReportParams,
        activeRepo,
        activeTopic,
        language,
        events,
        reposWithGitHubMetadata,
        searchQuery,
        searchQueryDebounced,
        subscribeModalOpen,
        subscribedToNewsletter,
        reportIDSetByTagName,
        getSetCurrentPageLink,
        reportBelongsToTag,
        activeAuthorType,
      ]
    );

    const updaters = useMemo<FCContextUpdaters>(
      () => ({
        setPage,
        setActiveReportID,
        setActiveRepo,
        setSearchQuery,
        setSearchQueryLiveAndDebounced,
        setSubscribeModalOpen,
        updateReportMetadata,
        setActiveAuthorType,
      }),
      [
        setPage,
        setActiveReportID,
        setActiveRepo,
        setSearchQuery,
        setSearchQueryLiveAndDebounced,
        updateReportMetadata,
        setActiveAuthorType,
      ]
    );

    return (
      <FCContext.Provider value={state}>
        <FCUpdaterContext.Provider value={updaters}>
          {children}
        </FCUpdaterContext.Provider>
      </FCContext.Provider>
    );
  }
);

export function useFCContext(): FCContextState {
  return useContext(FCContext);
}
export function useFCContextUpdaters(): FCContextUpdaters {
  return useContext(FCUpdaterContext);
}

const FCWithContext: React.FC = React.memo(() => {
  useHideNavOnPage();
  return (
    <FCContextProvider>
      <FC />
    </FCContextProvider>
  );
});

export default FCWithContext;

type FCProps = {};

function useForgeDocumentMeta({
  activeReportID,
  activeTag,
  homeCategoryName,
  tagsV2,
}: Pick<
  FCContextState,
  'activeReportID' | 'activeTag' | 'homeCategoryName' | 'tagsV2'
>): void {
  useEffect(() => {
    if (!isMountedInShell() || activeReportID != null) {
      return;
    }

    const tag =
      activeTag === homeCategoryName
        ? undefined
        : tagsV2.find(t => t.name === activeTag);
    setDocumentTitle(
      tag == null
        ? 'AI Articles, Tutorials & Reports by CoreWeave.'
        : `${tag.name} Articles & Tutorials by CoreWeave`,
      false
    );
    setDocumentDescription(
      tag == null
        ? 'Forge Reports: Where leading machine learning practitioners discover and share tutorials, papers, findings and reports.'
        : tag.description ||
            `Find ${tag.name} articles & tutorials from leading machine learning practitioners and AI builders.`
    );
  }, [activeReportID, activeTag, homeCategoryName, tagsV2]);
}

const FC: React.FC<FCProps> = React.memo(() => {
  const context = useFCContext();
  const {page, activeReportID, activeReportParams, activeRepo} = context;
  useForgeDocumentMeta(context);
  const {path} = useRouteMatch();
  const isForge = isMountedInShell();

  const {ref: pageRef} = useScrollIntoView<HTMLDivElement>(0);
  // useEffectExceptFirstRender(() => {
  //   scrollToTopOfPage();
  // }, [page, activeTag, activeReportID, activeRepo, activeTopic]);

  return (
    <>
      <S.Page data-test="fc-page">
        {!isForge && <FCTopNav />}
        {activeReportID == null && <FCIntro />}

        <S.FCPageContainer ref={pageRef} viewingReport={activeReportID != null}>
          <FCBreadcrumbs />
          <S.FCContent
            wider={activeRepo != null}
            reportMode={activeReportID != null}>
            {activeReportID != null ? (
              activeReportParams != null ? (
                <ReportPageContextProvider
                  path={path}
                  draftId={null}
                  entityName={activeReportParams.entityName}
                  projectName={activeReportParams.projectName}
                  reportId={activeReportParams.id}
                  reportName={activeReportParams.displayName}
                  isWBFeaturedReport>
                  <PanelBankContextProvider>
                    <Weave1PanelContextProvider>
                      <ReportPageContainer />
                    </Weave1PanelContextProvider>
                  </PanelBankContextProvider>
                </ReportPageContextProvider>
              ) : (
                <LoadingContent />
              )
            ) : activeRepo != null ? (
              <RepoPage />
            ) : ['blog', ...TAG_PAGES].includes(page) ? (
              <BlogPage />
            ) : page === `events` ? (
              <EventsPage />
            ) : page === `projects` ? (
              <ReposPage />
            ) : page === `topics` ? (
              <TopicPage />
            ) : null}
          </S.FCContent>
        </S.FCPageContainer>

        {!isForge && <FCCTA />}
        {isForge ? <FCFooter /> : <FCStandaloneFooter />}
      </S.Page>

      <SubscribeModal />
    </>
  );
});

type FCIntroProps = {};

const FCIntro: React.FC<FCIntroProps> = React.memo(() => {
  const {banner} = useFCContext();
  const isForge = isMountedInShell();

  if (banner && !banner.visible) {
    return null;
  }

  return (
    <S.Intro $forge={isForge}>
      <S.IntroContent>
        <div className="fully-connected-banner-may-2025">
          {/* oxlint-disable-next-line wandb/no-a-tags */}
          <a href={banner?.url ?? '#'} className="fc25-banner">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className="fcbanner-icon">
              <path
                d="M13.5 2.75C13.5 2.33579 13.1642 2 12.75 2C12.3358 2 12 2.33579 12 2.75V4.75C12 5.16421 12.3358 5.5 12.75 5.5C13.1642 5.5 13.5 5.16421 13.5 4.75V2.75Z"
                fill={isForge ? 'currentColor' : '#2B3038'}></path>
              <path
                d="M19.4403 4.55967C19.7332 4.85256 19.7332 5.32743 19.4403 5.62033L17.7303 7.33033C17.4374 7.62323 16.9625 7.62323 16.6697 7.33033C16.3768 7.03744 16.3768 6.56257 16.6697 6.26967L18.3797 4.55967C18.6726 4.26677 19.1474 4.26677 19.4403 4.55967Z"
                fill={isForge ? 'currentColor' : '#2B3038'}></path>
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.6503 6.11967C10.3574 5.82677 9.88253 5.82677 9.58964 6.11966C9.29674 6.41256 9.29674 6.88743 9.58963 7.18032L9.82253 7.41322L4.29673 14.2485C4.29152 14.2549 4.28642 14.2615 4.28146 14.268C3.83084 14.7209 3.42344 15.1515 3.10107 15.5701C2.76078 16.012 2.48003 16.4844 2.35421 17.0055C2.07953 18.143 2.60792 19.1586 3.64963 20.2003C4.6911 21.2418 5.70599 21.7719 6.84349 21.4974C7.36451 21.3717 7.83689 21.0908 8.27877 20.7503C8.55023 20.5411 8.82688 20.2961 9.11151 20.0266L11.2984 22.2135C11.5913 22.5064 12.0662 22.5064 12.3591 22.2135C12.652 21.9206 12.652 21.4457 12.3591 21.1528L10.2322 19.026L16.4309 14.0216L16.6596 14.2503C16.9525 14.5432 17.4274 14.5432 17.7203 14.2503C18.0132 13.9574 18.0132 13.4826 17.7203 13.1897L17.088 12.5574C17.0673 12.53 17.0445 12.5038 17.0198 12.4792L15.3937 10.8559C15.8224 10.1749 15.7178 9.27721 15.1403 8.69966C14.557 8.11632 13.6649 8.02021 12.9828 8.44927L11.3598 6.8292L11.3583 6.82768L10.6503 6.11967ZM15.3642 12.9549L9.17903 17.9484L5.89189 14.6613L10.8891 8.47982L15.3642 12.9549ZM4.88455 15.7996C4.65142 16.0471 4.45318 16.2728 4.2895 16.4853C4.01165 16.8461 3.86846 17.125 3.8123 17.3576C3.72302 17.7273 3.79846 18.2278 4.71029 19.1397C5.62342 20.0528 6.12305 20.1282 6.49165 20.0393C6.72369 19.9833 7.00226 19.8403 7.36319 19.5621C7.57608 19.3981 7.80227 19.1993 8.0504 18.9655L4.88455 15.7996Z"
                fill={isForge ? 'currentColor' : '#2B3038'}></path>
              <path
                d="M19.25 10.5C18.8358 10.5 18.5 10.8358 18.5 11.25C18.5 11.6642 18.8358 12 19.25 12H21.25C21.6642 12 22 11.6642 22 11.25C22 10.8358 21.6642 10.5 21.25 10.5H19.25Z"
                fill={isForge ? 'currentColor' : '#2B3038'}></path>
            </svg>
            <span className="fc-banner-message">{banner?.text ?? ''}</span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="24"
              viewBox="0 0 21 24"
              fill="none"
              className="arrow-icon">
              <path
                d="M17.0458 13C17.0458 13.0919 17.0262 13.1792 16.991 13.258L16.8878 13.4181C16.8791 13.428 16.87 13.4377 16.8606 13.4471L10.7584 19.5493C10.5114 19.7963 10.1111 19.7963 9.86416 19.5493C9.61723 19.3024 9.61723 18.9021 9.86416 18.6551L14.887 13.6323H3.99948C3.65028 13.6323 3.36719 13.3492 3.36719 13C3.36719 12.6508 3.65028 12.3677 3.99948 12.3677H14.887L9.86416 7.34484C9.61724 7.09792 9.61724 6.69757 9.86416 6.45065C10.1111 6.20372 10.5114 6.20372 10.7584 6.45064L16.8606 12.5529L16.862 12.5542C16.9202 12.6128 16.9646 12.6799 16.9951 12.7515C17.0278 12.8278 17.0458 12.9118 17.0458 13Z"
                fill={isForge ? 'currentColor' : '#2B3038'}></path>
            </svg>
          </a>
        </div>
        <style>
          {`
            .fully-connected-banner-may-2025 {
              display: flex;
              flex-direction: row;
              justify-content: center;
              align-items: center;
              width: 100%;
              padding: 20px;
            }

            .fc25-banner {
              height: 36px;
              border-radius: 36px;
              display: flex;
              font-size: 16px;
              width: auto;
              font-weight: 600;
              flex-direction: row;
              align-items: center;
              padding: 6px 10px;
              gap: 65px;
              background: ${
                isForge
                  ? CW_BLUE
                  : 'linear-gradient(270deg, #FFCC33 0%, #FFAD33 100%)'
              };
              flex-grow: 0;
              flex-shrink: 0;
              margin: 0;
              transition: all 250ms ease-out;
              flex-wrap: nowrap;
              text-decoration: none;
              background-size: ${isForge ? 'auto' : '200% 200%'};
              color: ${isForge ? '#fff' : '#000'};
            }

            .fc25-banner:hover {
              ${isForge ? `background: ${CW_BLUE_HOVER}; color: #fff;` : ''}
            }

            .fc25-banner .fcbanner-icon {
              width: 24px;
              height: 24px;
              filter: ${isForge ? 'none' : 'brightness(0) saturate(100%)'};
              flex-shrink: 0;
            }

            .fc25-banner .fc-banner-message {
              font-size: 16px;
              font-weight: 600;
              line-height: 1.4;
              flex: 1;
              text-align: center;
            }

            .fc25-banner .arrow-icon {
              width: 20px;
              height: 20px;
              filter: ${isForge ? 'none' : 'brightness(0) saturate(100%)'};
              flex-shrink: 0;
              transition: transform 0.2s ease;
            }

            .fc25-banner:hover .arrow-icon {
              transform: translateX(4px);
            }

            @media (max-width: 768px) {
              .fc25-banner {
                gap: 10px;
                padding: 6px 8px;
                max-width: 100%;
                height: auto;
              }

              .fc25-banner .fc-banner-message {
                font-size: 12px;
                text-align: left;
              }

              .fc25-banner .fcbanner-icon {
                width: 20px;
                height: 20px;
              }

              .fc25-banner .arrow-icon {
                width: 18px;
                height: 18px;
              }
            }
          `}
        </style>
      </S.IntroContent>
    </S.Intro>
  );
});

/**
 * Matches the `Home` breadcrumb the host writes into the JSON-LD for these
 * pages (frontends/app/host/src/meta.ts), so the two trails agree.
 */
const COREWEAVE_HOME_URL = 'https://www.coreweave.com/';

/**
 * Kept in step with the `PostalAddress` and `sameAs` entries in the JSON-LD
 * that the host writes for these pages (frontends/app/host/src/meta.ts).
 */
const COREWEAVE_ADDRESS_LINES = [
  `290 W Mt Pleasant Ave`,
  `Suite 4100`,
  `Livingston, NJ 07039`,
];

const FOOTER_SOCIAL_LINKS: FCFooterSocialLink[] = [
  {
    url: `https://www.linkedin.com/company/coreweave/`,
    label: `CoreWeave on LinkedIn`,
    Icon: LinkedInIcon,
  },
  {
    url: `https://x.com/CoreWeave`,
    label: `CoreWeave on X`,
    Icon: XIcon,
  },
];

type FCFooterSocialLink = {
  url: string;
  label: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
};

type FCFooterProps = {};

const FCFooter: React.FC<FCFooterProps> = React.memo(() => {
  return (
    <S.Footer>
      <S.FooterContent>
        <S.FooterAddress>
          <div>&copy; CoreWeave.</div>
          {COREWEAVE_ADDRESS_LINES.map(line => (
            <div key={line}>{line}</div>
          ))}
        </S.FooterAddress>

        <S.FooterMark aria-hidden="true">
          <CoreWeaveMark />
        </S.FooterMark>

        <S.FooterLinks>
          <S.FooterLegalLinks>
            <S.FooterLegalLink to={URLs.termsOfService()}>
              Terms of Service
            </S.FooterLegalLink>
            <S.FooterLegalLink to={URLs.privacyPolicy()}>
              Privacy Policy
            </S.FooterLegalLink>
          </S.FooterLegalLinks>
          <S.FooterSocialLinks>
            {FOOTER_SOCIAL_LINKS.map(({url, label, Icon}) => (
              <S.FooterSocialLink
                key={url}
                to={url}
                aria-label={label}
                title={label}>
                <Icon />
              </S.FooterSocialLink>
            ))}
          </S.FooterSocialLinks>
        </S.FooterLinks>
      </S.FooterContent>
    </S.Footer>
  );
});

const FCCTA = React.memo(() => {
  const viewer = useViewer();
  return (
    <S.CTA>
      <S.CTAContent>
        <S.CTAText>
          Iterate on AI agents
          <ResponsiveSpace />
          and models faster.
          <ResponsiveSpace />
          <S.GoldText>Try Weights & Biases today.</S.GoldText>
        </S.CTAText>
        <S.CTAButtons>
          {viewer == null && (
            <S.CTAButtonGold data-test="signup" to={URLs.login({signup: true})}>
              Sign up
            </S.CTAButtonGold>
          )}
          <S.CTAButtonWhite to={docURLs.gettingStarted}>
            Try W&B now
          </S.CTAButtonWhite>
        </S.CTAButtons>
      </S.CTAContent>
    </S.CTA>
  );
});

type FCFooterNavSection = {
  header: string;
  links: FCFooterNavLink[];
};

type FCFooterNavLink = {
  url: string;
  text: string;
  mobileOnly?: true;
};

const FOOTER_NAV_SECTIONS: FCFooterNavSection[] = [
  {
    header: `Products`,
    links: [
      {url: URLs.experimentTracking(), text: `Dashboard`},
      {url: URLs.sweeps(), text: `Sweeps`},
      {url: URLs.artifacts(), text: `Artifacts`},
      {url: URLs.reports(), text: `Reports`},
      {url: URLs.tables(), text: `Tables`},
    ],
  },
  {header: `Quickstart`, links: [{url: docURLs.root, text: `Documentation`}]},
  {
    header: `Resources`,
    links: [
      {url: URLs.courses(), text: `Courses`},
      {url: URLs.tutorials(), text: `Tutorials`},
    ],
  },
  {
    header: `W&B`,
    links: [
      {url: URLs.company(), text: `About Us`},
      {url: URLs.authors(), text: `Authors`},
      {url: URLs.contact(), text: `Contact`},
      {url: URLs.termsOfService(), text: `Terms of Service`, mobileOnly: true},
      {url: URLs.privacyPolicy(), text: `Privacy Policy`, mobileOnly: true},
    ],
  },
];

const FCStandaloneFooter: React.FC = React.memo(() => {
  const {subscribedToNewsletter, loadingNewsletterSubscription} =
    useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();
  return (
    <S.StandaloneFooter>
      <S.StandaloneFooterContent>
        <S.StandaloneFooterMain>
          <S.StandaloneFooterLeftColumn>
            <S.StandaloneFooterLogo>
              <S.StandaloneFooterLogoImage
                src={publicAssetUrl('/logo-transparent.png')}
              />
              Weights & Biases
            </S.StandaloneFooterLogo>
            {!subscribedToNewsletter && !loadingNewsletterSubscription && (
              <S.StandaloneFooterSubscribe>
                Get weekly updates with the latest ML news.
                <S.StandaloneFooterSubscribeButton
                  onClick={() => setSubscribeModalOpen(true)}>
                  Subscribe
                </S.StandaloneFooterSubscribeButton>
              </S.StandaloneFooterSubscribe>
            )}
          </S.StandaloneFooterLeftColumn>
          <S.StandaloneFooterNav>
            {FOOTER_NAV_SECTIONS.map(({header, links}, i) => (
              // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
              <S.StandaloneFooterNavColumn key={i}>
                <S.StandaloneFooterNavColumnHeader>
                  {header}
                </S.StandaloneFooterNavColumnHeader>
                <S.StandaloneFooterNavColumnLinks>
                  {links.map(({url, text, mobileOnly}, j) => (
                    // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                    <React.Fragment key={j}>
                      {j > 0 && <S.StandaloneFooterNavLinkDivider />}
                      <S.StandaloneFooterNavColumnLink
                        to={url}
                        mobileOnly={mobileOnly}>
                        {text}
                      </S.StandaloneFooterNavColumnLink>
                    </React.Fragment>
                  ))}
                </S.StandaloneFooterNavColumnLinks>
              </S.StandaloneFooterNavColumn>
            ))}
          </S.StandaloneFooterNav>
        </S.StandaloneFooterMain>
        <S.StandaloneFooterFooter>
          <S.StandaloneFooterFooterContent>
            <S.StandaloneFooterFooterLeftColumn>
              Copyright &copy;{new Date().getFullYear()} Weights & Biases. All
              rights reserved.
            </S.StandaloneFooterFooterLeftColumn>
            <S.StandaloneFooterFooterRightColumn>
              <S.StandaloneFooterFooterLink to={URLs.termsOfService()}>
                Terms of Service
              </S.StandaloneFooterFooterLink>
              <S.StandaloneFooterFooterLink to={URLs.privacyPolicy()}>
                Privacy Policy
              </S.StandaloneFooterFooterLink>
            </S.StandaloneFooterFooterRightColumn>
          </S.StandaloneFooterFooterContent>
        </S.StandaloneFooterFooter>
      </S.StandaloneFooterContent>
    </S.StandaloneFooter>
  );
});

type FCBreadcrumbSegment = {
  text: string;
  linkTo?: string;
};

type FCBreadcrumbsProps = {};

const FCBreadcrumbs: React.FC<FCBreadcrumbsProps> = React.memo(() => {
  const isForge = isMountedInShell();
  const {
    page,
    activeReportParams,
    activeRepo,
    activeTag,
    activeTopic,
    homeCategoryName,
  } = useFCContext();
  const {primaryTag} = useReportTags(activeReportParams?.id ?? null);

  const segments: FCBreadcrumbSegment[] = useMemo(() => {
    if (activeReportParams != null) {
      return [
        {text: getLabelForFCPage(page), linkTo: getSetPageLink(page)},
        ...(primaryTag != null
          ? [
              {
                text: primaryTag.name,
                linkTo: getSetActiveTagLink(primaryTag.linkForm),
              },
            ]
          : []),
      ];
    }
    if (activeRepo != null) {
      return [
        {text: getLabelForFCPage(page), linkTo: getSetPageLink(page)},
        {text: activeRepo},
      ];
    }
    if (!isForge) {
      return [];
    }
    if (activeTopic != null) {
      return [{text: activeTopic}];
    }
    // Listing pages. The home page's crumb would only link to itself, so it
    // gets no trail; every other listing links back through the root crumb.
    if (page !== MAIN_PAGE) {
      return [{text: getLabelForFCPage(page)}];
    }
    if (activeTag === homeCategoryName) {
      return [];
    }
    return [
      {text: getLabelForFCPage(page), linkTo: getSetPageLink(page)},
      {text: activeTag},
    ];
  }, [
    page,
    activeReportParams,
    activeRepo,
    activeTag,
    activeTopic,
    homeCategoryName,
    primaryTag,
    isForge,
  ]);

  const width =
    activeReportParams != null
      ? 1072
      : activeRepo != null
        ? 1280
        : S.MAX_PAGE_WIDTH;

  if (segments.length === 0) {
    return null;
  }

  return (
    <S.Breadcrumbs width={width}>
      {isForge && (
        <>
          <S.BreadcrumbsHomeLink
            to={COREWEAVE_HOME_URL}
            newTab={false}
            aria-label="CoreWeave home"
            title="CoreWeave home">
            <CoreWeaveMark />
          </S.BreadcrumbsHomeLink>
          <S.BreadcrumbsSeparator>{`>`}</S.BreadcrumbsSeparator>
        </>
      )}
      <S.BreadcrumbsLink to={URLs.fullyConnected()}>
        {isForge ? 'Forge Reports' : 'W&B Fully Connected'}
      </S.BreadcrumbsLink>
      {segments.map(({text, linkTo}, i) => (
        // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
        <React.Fragment key={i}>
          <S.BreadcrumbsSeparator>{`>`}</S.BreadcrumbsSeparator>
          {linkTo ? (
            <S.BreadcrumbsLink to={linkTo}>{text}</S.BreadcrumbsLink>
          ) : (
            <S.BreadcrumbsSpan>{text}</S.BreadcrumbsSpan>
          )}
        </React.Fragment>
      ))}
    </S.Breadcrumbs>
  );
});

type BlogPageProps = {};

const BlogPage: React.FC<BlogPageProps> = React.memo(() => {
  const {
    activeTag,
    tagsV2,
    currentPage,
    reports: pagedReports, // Sliced, filtered, excludes 'configured featured'
    newReports, // Filtered
    loadingReports,
    activeAuthorType,
  } = useFCContext();
  const {setActiveAuthorType} = useFCContextUpdaters();

  const prevPage = usePrevious(currentPage);
  const {ref: reportsHeaderRef, trigger: scrollToReportsHeader} =
    useScrollIntoView<HTMLHeadingElement>();
  useEffect(() => {
    if (prevPage != null && currentPage !== prevPage) {
      scrollToReportsHeader();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Banner logic
  const featuredReports = useMemo(() => {
    return newReports.length > 0 ? newReports : pagedReports.slice(0, 5);
  }, [newReports, pagedReports]);

  // Grid logic
  const gridReports = useMemo(() => {
    // If we used newReports for banner, they are already excluded from pagedReports (by useFCReports).
    // So we just return pagedReports.
    if (newReports.length > 0) {
      return pagedReports;
    }
    // If we used pagedReports for banner (fallback), we must exclude them from grid.
    if (currentPage > 1) {
      return pagedReports;
    }

    const featuredIds = new Set(featuredReports.map(r => r.id));
    return pagedReports.filter(r => !featuredIds.has(r.id));
  }, [pagedReports, featuredReports, newReports, currentPage]);

  return (
    <S.Columns>
      <S.LeftColumn>
        {!loadingReports && featuredReports.length > 0 && currentPage === 1 && (
          <FeaturedBanner reports={featuredReports} />
        )}

        <div ref={reportsHeaderRef} />

        <TopicFilters
          activeAuthorType={activeAuthorType}
          onAuthorTypeChange={setActiveAuthorType}
          activeTag={activeTag}
          tagsV2={tagsV2}
        />

        {loadingReports ? (
          <LoadingContent />
        ) : (
          <>
            <div style={{marginTop: '24px'}}>
              <S.PostGrid>
                {gridReports.map(r => (
                  <PostCard key={r.id} report={r} tagsV2={tagsV2} />
                ))}
              </S.PostGrid>
            </div>
            <Pagination />
          </>
        )}
      </S.LeftColumn>
    </S.Columns>
  );
});

const EVENT_PAGE_SIZE = 9;

type EventsPageProps = {};

const EventsPage: React.FC<EventsPageProps> = React.memo(() => {
  const {
    loadingEvents,
    events,
    subscribedToNewsletter,
    loadingNewsletterSubscription,
  } = useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [startDateFilter, setStartDateFilter] = useState<Date | null>(null);
  const [endDateFilter, setEndDateFilter] = useState<Date | null>(null);
  const [region] = useState<string>('');
  const [topic] = useState<string>('');

  const [fcEvents, setFCEvents] = useState(events);
  useEffect(() => {
    if (!loadingEvents) {
      setFCEvents(events);
    }
  }, [loadingEvents, events]);

  const [pastEventsShown, setPastEventsShown] = useState(EVENT_PAGE_SIZE);
  const [futureEventsShown, setFutureEventsShown] = useState(EVENT_PAGE_SIZE);
  const [pastEvents, futureEvents] = useMemo(() => {
    return [
      // Events are already returned from `useBizzaboEvents` sorted
      // latest first, which is what we want for past events.
      fcEvents.filter(e => new Date(e.startDate).valueOf() <= Date.now()),

      // For future events, we want to sort them earliest first instead.
      _.sortBy(
        fcEvents.filter(e => new Date(e.startDate).valueOf() > Date.now()),
        e => {
          const d = new Date(e.startDate);
          return d.valueOf();
        }
      ),
    ];
  }, [fcEvents]);
  const showPastShowMoreButton = pastEventsShown < pastEvents.length;
  const showFutureShowMoreButton = futureEventsShown < futureEvents.length;

  // REGION
  const regionOptions = useMemo(() => {
    const allRegions = _.uniq(
      _.compact(events.flatMap(e => e.timezone.split('/')[0]))
    );
    const regionOpts = allRegions.map(r => ({
      text: r,
      value: r.toLowerCase(),
    }));
    const allOpts = [{text: '-- Region --', value: ''}, ...regionOpts];
    return allOpts;
  }, [events]);
  const regionValToText = useMemo(() => {
    const regionMap = new Map<string, string>();
    regionOptions.forEach(o => regionMap.set(o.value, o.text));
    return regionMap;
  }, [regionOptions]);

  // TOPIC
  const topicOptions = useMemo(() => {
    const allTopics = _.uniq(_.compact(events.flatMap(e => e.type)));
    const topicOpts = allTopics.map(t => ({
      text: t,
      value: t.toLowerCase().replace(/&/g, 'and').replace(/ /g, '-'),
    }));
    const allOpts = [{text: '-- Topic --', value: ''}, ...topicOpts];
    return allOpts;
  }, [events]);
  const topicValToText = useMemo(() => {
    const topicMap = new Map<string, string>();
    topicOptions.forEach(o => topicMap.set(o.value, o.text));
    return topicMap;
  }, [topicOptions]);

  // SEARCH
  const searchEvents = useCallback(() => {
    let results = events;
    // query search
    if (searchQuery !== '') {
      results = fuzzyMatchWithMapping(results, searchQuery, e => e.name);
    }
    // from date
    if (startDateFilter != null) {
      results = results.filter(e => {
        const eventStart = new Date(e.startDate);
        return eventStart >= startDateFilter;
      });
    }
    // to date
    if (endDateFilter != null) {
      results = results.filter(e => {
        const eventStart = new Date(e.startDate);
        return eventStart <= endDateFilter;
      });
    }
    // region of choice
    if (region !== '') {
      results = results.filter(e =>
        e.timezone.includes(regionValToText.get(region) ?? '')
      );
    }
    // topic of choice
    if (topic !== '') {
      results = results.filter(e =>
        e.type?.includes(topicValToText.get(topic) ?? '')
      );
    }
    setFCEvents(results);
    setPastEventsShown(EVENT_PAGE_SIZE);
    setFutureEventsShown(EVENT_PAGE_SIZE);
  }, [
    events,
    regionValToText,
    topicValToText,
    searchQuery,
    startDateFilter,
    endDateFilter,
    region,
    topic,
  ]);

  useEffect(() => {
    searchEvents();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, startDateFilter, endDateFilter]);

  return (
    <>
      <S.EventsContainer>
        <S.EventsControls>
          <S.EventsControlsInput
            placeholder={`Search events`}
            icon={<S.EventsControlsIcon name="search" />}
            value={searchQuery}
            onChange={
              ((e, {value}) =>
                setSearchQuery(value)) as StrictInputProps['onChange']
            }
          />
          <S.EventsControlsDatetimeContainer>
            <S.EventsControlsDatetime
              placeholder="Select from date"
              value={startDateFilter ?? undefined}
              onChange={d => {
                setStartDateFilter(d);
              }}
            />
          </S.EventsControlsDatetimeContainer>
          <S.EventsControlsDatetimeContainer>
            <S.EventsControlsDatetime
              placeholder="Select to date"
              value={endDateFilter ?? undefined}
              onChange={d => {
                setEndDateFilter(d);
              }}
            />
          </S.EventsControlsDatetimeContainer>
          {/* <S.EventsControlsDropdown
            selection
            placeholder={`-- Region --`}
            icon={<S.EventsControlsIcon name="chevron-down" />}
            options={regionOptions}
            value={region}
            onChange={
              ((e, {value}) =>
                setRegion(value as string)) as StrictDropdownProps['onChange']
            }
          />
          <S.EventsControlsDropdown
            selection
            placeholder={`-- Topic --`}
            icon={<S.EventsControlsIcon name="chevron-down" />}
            options={topicOptions}
            value={topic}
            onChange={
              ((e, {value}) =>
                setTopic(value as string)) as StrictDropdownProps['onChange']
            }
          />
          <S.EventsButton onClick={searchEvents}>Search</S.EventsButton> */}
        </S.EventsControls>
        {loadingEvents ? (
          <LoadingContent />
        ) : (
          <>
            {futureEvents.length > 0 && (
              <S.EventsCollection>
                <S.EventsHeader>Upcoming events</S.EventsHeader>
                <S.EventsText>
                  Check out our upcoming webinars, paper reading groups,
                  tutorials, and more! We host multiple events a week and
                  they're always free for our community. Hope to see you there!
                </S.EventsText>
                <S.EventEntries>
                  {futureEvents
                    .slice(0, futureEventsShown)
                    .map(({url, startDate, name, fcMetadata}, i) => (
                      // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                      <S.EventEntry key={i} href={url}>
                        <S.EventEntryImage
                          src={imgSrcWithDefault(fcMetadata?.imageURL)}
                        />
                        <S.EventEntryDate>
                          {new Date(startDate).toDateString()}
                        </S.EventEntryDate>
                        <S.EventEntryName>{name}</S.EventEntryName>
                      </S.EventEntry>
                    ))}
                </S.EventEntries>
                <S.ShowMoreContainer>
                  {showFutureShowMoreButton && (
                    <S.ShowMoreButton
                      onClick={() =>
                        setFutureEventsShown(prev => prev + EVENT_PAGE_SIZE)
                      }>
                      Show more
                    </S.ShowMoreButton>
                  )}
                </S.ShowMoreContainer>
              </S.EventsCollection>
            )}
            {pastEvents.length > 0 && (
              <S.EventsCollection>
                <S.EventsHeader>Rewatch past events</S.EventsHeader>
                <S.EventsText>
                  All our previous events are free to watch. Check out anything
                  you've missed in the past couple months.
                </S.EventsText>
                <S.EventEntries>
                  {pastEvents
                    .slice(0, pastEventsShown)
                    .map(({url, startDate, name, fcMetadata}, i) => (
                      // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
                      <S.EventEntry key={i} href={url}>
                        <S.EventEntryImage
                          src={imgSrcWithDefault(fcMetadata?.imageURL)}
                        />
                        <S.EventEntryName>{name}</S.EventEntryName>
                      </S.EventEntry>
                    ))}
                </S.EventEntries>
                <S.ShowMoreContainer>
                  {showPastShowMoreButton && (
                    <S.ShowMoreButton
                      onClick={() =>
                        setPastEventsShown(prev => prev + EVENT_PAGE_SIZE)
                      }>
                      Show more
                    </S.ShowMoreButton>
                  )}
                </S.ShowMoreContainer>
              </S.EventsCollection>
            )}
          </>
        )}
      </S.EventsContainer>
      {!subscribedToNewsletter && !loadingNewsletterSubscription && (
        <S.EventsCTA>
          <S.EventsCTAText>
            Don't want to miss any upcoming W&B Events?
            <br />
            Subscribe to reminders!
          </S.EventsCTAText>
          <S.IntroButtonImg
            src={publicAssetUrl('/fc/button-subscribe.png')}
            onClick={() => setSubscribeModalOpen(true)}
          />
        </S.EventsCTA>
      )}
    </>
  );
});

type ReposPageProps = {};

const ReposPage: React.FC<ReposPageProps> = React.memo(() => {
  const {loading, reposWithGitHubMetadata: repos} = useFCContext();
  const [reposSearch, setReposSearch] = useState('');

  const filteredRepos = useMemo(() => {
    if (reposSearch === '') {
      return repos;
    }
    return fuzzyMatchWithMapping(repos, reposSearch, e =>
      [e.url, e.name, e.description].join(' ')
    );
  }, [reposSearch, repos]);
  return (
    <Container>
      <Header style={{textAlign: 'center'}} size="huge">
        Trending Projects
        <Header.Subheader>
          Browse popular machine learning projects
        </Header.Subheader>
      </Header>
      <div style={{paddingBottom: 10}}>
        <Input
          icon="search"
          value={reposSearch}
          placeholder="Search..."
          onChange={(e, {value}) => {
            setReposSearch(value);
          }}
        />
      </div>
      {loading ? <Loader name="repos-page" /> : <Repos rows={filteredRepos} />}
    </Container>
  );
});

type ReposProps = {
  rows: RepoWithGitHubMetadata[];
};

const Repos: React.FC<ReposProps> = React.memo(({rows}) => {
  return (
    <S.RepoList>
      {rows.map(repo => {
        const {id, name, description, stars, forks, imageURL} = repo;
        return (
          <S.RepoListItem
            key={id}
            to={getSetActiveRepoLink(repo)}
            onClick={() => {
              window.analytics?.track(`FC Project Clicked`, {
                location: `FC projects page`,
                projectID: id,
                projectName: name,
              });
            }}>
            <S.RepoListItemImage src={imageURL} />
            <S.RepoListItemContent>
              <S.RepoListItemHeaderRow>
                <S.RepoListItemHeader>{name}</S.RepoListItemHeader>
                <S.RepoListItemStats>
                  <S.RepoListItemStat>
                    <Icon name="star" />
                    {stars}
                  </S.RepoListItemStat>
                  <S.RepoListItemStat>
                    <Icon name="branch-commit" />
                    {forks}
                  </S.RepoListItemStat>
                  {/* <S.RepoListItemStat>
                  <Icon name="find" />
                  {watching}
                </S.RepoListItemStat>
                <S.RepoListItemStat>
                  <Icon name="fire" />
                  {0}
                </S.RepoListItemStat> */}
                </S.RepoListItemStats>
              </S.RepoListItemHeaderRow>
              <S.RepoListItemBody>{description}</S.RepoListItemBody>
            </S.RepoListItemContent>
          </S.RepoListItem>
        );
      })}
    </S.RepoList>
  );
});

type RepoGitHubMetadata = {
  stars: number;
  watching: number;
  forks: number;
};

type RepoWithGitHubMetadata = RepoWithUsage & RepoGitHubMetadata;

const RepoPage: React.FC = React.memo(() => {
  const {reposWithGitHubMetadata: repos, activeRepo} = useFCContext();

  const repo = useMemo(
    () => repos.find(r => r.linkForm === activeRepo),
    [repos, activeRepo]
  );

  const links = useMemo(
    () => repo?.links.filter(l => !l.pending) ?? [],
    [repo]
  );

  if (repo == null) {
    return null;
  }

  return (
    <PageWithSidebar
      sidebar={<RepoSidebar repo={repo} />}
      sidebarStyle={{minHeight: '100%'}}>
      <S.RepoContent>
        <S.RepoCustomContent>
          <Markdown content={repo.customContent} />
        </S.RepoCustomContent>
        {links.length > 0 && (
          <>
            <div className="header-wrapper">
              <ImageIconHeader icon="gallery" text="Links" />
            </div>
            <div className="report-showcase">
              <Card.Group itemsPerRow={2}>
                {links.map(({id, url, name, description, imageURL}) => {
                  return (
                    <Link
                      key={id}
                      to={url}
                      className={`ui card report-showcase-item`}>
                      <Card.Content className="report-showcase-item-content">
                        <img
                          src={imageURL}
                          className={`report-showcase-item-image`}
                          alt={`link thumbnail`}
                        />
                        <div className="report-showcase-item-text">
                          <Card.Header className="report-showcase-item-title">
                            {name}
                          </Card.Header>
                          <Card.Description className="report-showcase-item-description">
                            {description}
                          </Card.Description>
                        </div>
                      </Card.Content>
                    </Link>
                  );
                })}
              </Card.Group>
            </div>
          </>
        )}
      </S.RepoContent>
    </PageWithSidebar>
  );
});

type RepoSidebarProps = {repo: RepoWithGitHubMetadata};

const RepoSidebar: React.FC<RepoSidebarProps> = React.memo(
  ({repo: {name, description, imageURL, stars, watching, forks}}) => {
    return (
      <div className="profile-sidebar">
        <div className="inner-content">
          <S.RepoImage src={imageURL} />
          <S.RepoSidebarName>{name}</S.RepoSidebarName>
          <S.RepoDescription>{description}</S.RepoDescription>
          <S.RepoGitHubMetadata>
            <S.RepoGitHubMetadataRow>
              <S.RepoGitHubMetadataIcon name="star" /> {stars}
            </S.RepoGitHubMetadataRow>
            <S.RepoGitHubMetadataRow>
              <S.RepoGitHubMetadataIcon name="branch-commit" />
              {forks}
            </S.RepoGitHubMetadataRow>
          </S.RepoGitHubMetadata>
        </div>
      </div>
    );
  }
);

async function getGitHubMetadataForRepo({
  url,
}: RepoWithUsage): Promise<RepoGitHubMetadata> {
  const [owner, repoName] = url.split('/').slice(-2);
  // oxlint-disable-next-line wandb/no-unprefixed-urls
  const resp = await fetch(`https://api.github.com/repos/${owner}/${repoName}`);
  const json = await resp.json();
  return {
    stars: json.stargazers_count,
    watching: json.watchers_count,
    forks: json.forks_count,
  };
}

type TopicPageProps = {};

const TopicPage: React.FC<TopicPageProps> = React.memo(() => {
  const {tagsV2, activeTopic, reportIDSetByTagName} = useFCContext();
  if (activeTopic == null) {
    return null;
  }

  const topic = tagsV2.find(t => t.linkForm === activeTopic);
  if (topic == null) {
    return null;
  }

  const topicChildren = _.sortBy(
    tagsV2.filter(t => t.parent === topic.id),
    t => -(reportIDSetByTagName.get(t.name)?.size ?? 0)
  );

  return (
    <>
      <S.TopicPageHeader>{topic.name}</S.TopicPageHeader>
      <S.TopicPageTags>
        {topicChildren.map(({id, name, linkForm, imageURL}) => (
          <S.TopicPageTag key={id} to={getSetActiveTagLink(linkForm)}>
            <S.TopicPageTagImage src={imgSrcWithDefault(imageURL)} />
            <S.TopicPageTagName>{name}</S.TopicPageTagName>
          </S.TopicPageTag>
        ))}
      </S.TopicPageTags>
    </>
  );
});

type SubscribeModalProps = {};

const SubscribeModal: React.FC<SubscribeModalProps> = React.memo(() => {
  const {subscribeModalOpen} = useFCContext();
  const {setSubscribeModalOpen} = useFCContextUpdaters();

  const viewer = useViewer();
  const loggedIn: boolean = useMemo(() => viewer != null, [viewer]);

  const [createNewsletterSubscription] =
    useCreateNewsletterSubscriptionMutation({
      context: propagateErrorsContext(),
    });

  const subscriptionInputRef = useRef<Input>(null);

  const [subscriptionInput, setSubscriptionInput] = useState('');
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');

  const createSubscription = useCallback(async () => {
    try {
      if (!loggedIn && !isValidEmail(subscriptionInput)) {
        setSubscribeError('Please enter a valid email.');
        subscriptionInputRef.current?.focus();
        return;
      }

      const mutationVars = loggedIn ? {} : {email: subscriptionInput};
      await createNewsletterSubscription({variables: mutationVars});

      setSubscribeSuccess(true);
      setSubscriptionInput('');
      setSubscribeError('');
    } catch (err) {
      console.error(err);
      subscriptionInputRef.current?.focus();
      setSubscriptionInput('');
      setSubscribeError('Oops, something went wrong.');
    }
  }, [loggedIn, subscriptionInput, createNewsletterSubscription]);

  if (!subscribeModalOpen) {
    return null;
  }
  return (
    <S.SubscribeModal open onClose={() => setSubscribeModalOpen(false)}>
      <S.SubscribeSection>
        <S.SubscribeHeader>
          Get weekly updates with the latest ML news.
        </S.SubscribeHeader>
        {subscribeSuccess ? (
          <S.SubscribeSuccessMessage>
            <Icon name="checkmark" />
            Subscribed!
          </S.SubscribeSuccessMessage>
        ) : (
          <S.SubscribeInputContainer>
            <S.SubscribeInputMessageContainer loggedIn={loggedIn}>
              <S.SubscribeInput
                placeholder="Email Address"
                invalid={subscribeError !== ''}
                value={subscriptionInput}
                ref={subscriptionInputRef}
                onChange={
                  ((e, {value}) =>
                    setSubscriptionInput(value)) as StrictInputProps['onChange']
                }
              />
              <S.SubscribeInvalidMessage invalid={subscribeError !== ''}>
                {subscribeError}
              </S.SubscribeInvalidMessage>
            </S.SubscribeInputMessageContainer>
            <S.SubscribeButton
              content="Subscribe"
              onClick={createSubscription}
            />
          </S.SubscribeInputContainer>
        )}
      </S.SubscribeSection>
    </S.SubscribeModal>
  );
});

type ResponsiveProps = {desktop: ReactNode; mobile: ReactNode};

const Responsive: React.FC<ResponsiveProps> = React.memo(
  ({desktop, mobile}) => {
    const {width: screenWidth} = useWindowSize();
    const isDesktop = screenWidth > S.TABLET_WIDTH;
    return <>{isDesktop ? desktop : mobile}</>;
  }
);

type ResponsiveSpaceProps = {
  children?: React.ReactNode;
};

const ResponsiveSpace: React.FC<ResponsiveSpaceProps> = React.memo(() => {
  return <Responsive desktop={` `} mobile={<br />} />;
});

type DesktopProps = {children?: React.ReactNode};

const Desktop: React.FC<DesktopProps> = React.memo(({children}) => {
  return <Responsive desktop={children} mobile={null} />;
});

type FCReportsResult = {
  loadingReports: boolean;
  newReports: ReportMetadata[];
  popularReports: ReportMetadata[];
  reports: ReportMetadata[];
  updateReportMetadata: (
    id: string,
    updateFn: (r: ReportMetadata) => void
  ) => void;
};

function useFCReports({
  reportIDsWithTagV2IDs,
  tagsV2,
  activeTag,
  language,
  searchQueryDebounced,
  reportBelongsToTag,
}: Pick<
  FCContextState,
  | 'reportIDsWithTagV2IDs'
  | 'tagsV2'
  | 'activeTag'
  | 'language'
  | 'searchQueryDebounced'
  | 'reportBelongsToTag'
>): FCReportsResult {
  const {
    loading: reportMetadataLoading,
    reportMetadatas,
    updateReportMetadata,
  } = useReportMetadataWithCache({
    reportIDsWithTagV2IDs,
    tags: tagsV2,
    activeTag,
    fetchAll: searchQueryDebounced !== '',
    reportBelongsToTag,
  });

  const reports = useMemo(
    () => filterAndSortReports(reportMetadatas, language),
    [reportMetadatas, language]
  );

  const {newReports, popularReports, notFeaturedReports} = useMemo(
    () =>
      splitReportsByFeatured({
        reports,
        tags: tagsV2,
        activeTag,
        language,
        reportBelongsToTag,
      }),
    [reports, tagsV2, activeTag, language, reportBelongsToTag]
  );

  const searchedReports = useMemo(() => {
    if (searchQueryDebounced === '') {
      return notFeaturedReports;
    }
    return fuzzyMatchWithMapping(
      reports,
      searchQueryDebounced,
      (r: ReportMetadata) => r.displayName
    );
  }, [notFeaturedReports, reports, searchQueryDebounced]);

  return {
    loadingReports: reportMetadataLoading,
    newReports,
    popularReports,
    reports: searchedReports,
    updateReportMetadata,
  };
}

function filterAndSortReports(
  reports: ReportMetadata[],
  language: Language
): ReportMetadata[] {
  return _.sortBy(
    reports
      .filter(r => !r.pending)
      .filter(reportIsPastPublishDate)
      .filter(r => r.language === language),
    r => {
      const addedAtTS = new Date(r.addedAt ?? '').valueOf();
      return -addedAtTS;
    }
  );
}

function reportIsPastPublishDate(r: ReportMetadata): boolean {
  if (r.addedAt == null) {
    return false;
  }
  const addedAt = new Date(r.addedAt);
  const now = new Date();
  return now >= addedAt;
}

type ReportsSplitByFeatured = {
  newReports: ReportMetadata[];
  popularReports: ReportMetadata[];
  notFeaturedReports: ReportMetadata[];
};

type SplitReportsByFeaturedParams = {
  reports: ReportMetadata[];
  tags: TagV2[];
  activeTag: string;
  language: Language;
  reportBelongsToTag: (reportID: string, tagName: string) => boolean;
};

function splitReportsByFeatured({
  reports,
  tags,
  activeTag,
  language,
  reportBelongsToTag,
}: SplitReportsByFeaturedParams): ReportsSplitByFeatured {
  const tag = tags.find(t => t.name === activeTag);
  if (tag == null) {
    return {newReports: [], popularReports: [], notFeaturedReports: []};
  }

  // const announcementReportIDSet = new Set(
  //   reports.filter(r => r.announcement).map(r => r.id)
  // );
  // const categoryNameSet = new Set(categories.map(t => t.name));
  // const tagIsCategory = (tagName: string) => categoryNameSet.has(tagName);
  // const announcementAndFeaturedIDs = _.uniq([
  //   // only show announcements when active tag is a category
  //   ...(tagIsCategory(activeTag) ? announcementReportIDSet : []),
  //   ...tag.featuredReportIDs,
  // ]);
  // const announcementAndFeaturedIDSet = new Set(announcementAndFeaturedIDs);

  const reportsByID = new Map<string, ReportMetadata>(
    reports.map(r => [r.id, r])
  );
  const dontIncludeInPrimaryListIDSet = new Set(
    // We include popular reports in the primary list because otherwise
    // they may be missed if users never click on `Popular`
    tag.featuredReports.filter(r => r.category === `new`).map(r => r.reportID)
  );
  const tagReports = reports.filter(r => reportBelongsToTag(r.id, activeTag));

  return {
    newReports: tag.featuredReports
      .filter(r => r.category === `new`)
      .map(r => reportsByID.get(r.reportID))
      .filter(isNotNullOrUndefined)
      .filter(r => r.language === language),
    popularReports: tag.featuredReports
      .filter(r => r.category === `popular`)
      .map(r => reportsByID.get(r.reportID))
      .filter(isNotNullOrUndefined)
      .filter(r => r.language === language),
    notFeaturedReports: tagReports.filter(
      ({id}) => !dontIncludeInPrimaryListIDSet.has(id)
    ),
  };
}

type PaginationResult<T> = {
  currentItems: T[];
  minPage: number;
  maxPage: number;
};

type PaginationParams<T> = {
  loadingItems: boolean;
  allItems: T[];
  itemsPerPage: number;
  currentPage: number;
  setCurrentPage: (n: number) => void;
};

function usePagination<T>({
  loadingItems,
  allItems,
  itemsPerPage,
  currentPage,
  setCurrentPage,
}: PaginationParams<T>): PaginationResult<T> {
  const total = allItems.length;
  const minPage = 1;
  const maxPage = Math.max(Math.ceil(total / itemsPerPage), 1);

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return allItems.slice(startIndex, startIndex + itemsPerPage);
  }, [allItems, currentPage, itemsPerPage]);

  // Clamp to last page if out of bounds
  useLayoutEffect(() => {
    if (!loadingItems && currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingItems, currentPage, maxPage]);

  return {
    currentItems,
    minPage,
    maxPage,
  };
}

type PaginationProps = {};

const Pagination: React.FC<PaginationProps> = React.memo(() => {
  const location = useLocation();
  const {currentPage, minPage, maxPage, getSetCurrentPageLink} = useFCContext();

  const pagesWithEllipses: Array<number | 'ellipsis'> = useMemo(() => {
    if (maxPage - minPage < 5) {
      return _.range(minPage, maxPage + 1);
    }
    if (currentPage <= minPage + 2) {
      return [minPage, minPage + 1, minPage + 2, 'ellipsis', maxPage];
    }
    if (currentPage >= maxPage - 2) {
      return [minPage, 'ellipsis', maxPage - 2, maxPage - 1, maxPage];
    }
    return [minPage, 'ellipsis', currentPage, 'ellipsis', maxPage];
  }, [currentPage, minPage, maxPage]);

  const nextPageLink =
    currentPage < maxPage ? getSetCurrentPageLink(currentPage + 1) : undefined;
  const prevPageLink =
    currentPage > minPage ? getSetCurrentPageLink(currentPage - 1) : undefined;

  const renderPage = (p: number | 'ellipsis', i: number) => {
    if (p === 'ellipsis') {
      return (
        <S.PaginationPageDisabled key={i} disabled>
          <S.PaginationEllipsis>...</S.PaginationEllipsis>
        </S.PaginationPageDisabled>
      );
    }
    return (
      <S.PaginationPage
        key={i}
        to={getSetCurrentPageLink(p)}
        active={currentPage === p}>
        {p}
      </S.PaginationPage>
    );
  };

  return (
    <S.PaginationContainer>
      <S.Pagination>
        <S.PaginationEdge
          to={prevPageLink ?? location}
          disabled={prevPageLink == null}>
          <S.PaginationIcon name={`chevron-back`} />
          <Desktop>Previous</Desktop>
        </S.PaginationEdge>
        <S.PaginationPages>
          {pagesWithEllipses.map(renderPage)}
        </S.PaginationPages>
        <S.PaginationEdge
          to={nextPageLink ?? location}
          disabled={nextPageLink == null}>
          <Desktop>Next</Desktop>
          <S.PaginationIcon name={`chevron-next`} />
        </S.PaginationEdge>
      </S.Pagination>
    </S.PaginationContainer>
  );
});

type LoadingContentProps = {};

const LoadingContent: React.FC<LoadingContentProps> = React.memo(() => {
  return (
    <S.LoaderContainer>
      <S.Loader name="loading-content" />
    </S.LoaderContainer>
  );
});

type StateFromURL = {
  page: FCPage;
  activeTag: string;
  activeReportID: string | null;
  activeRepo: string | null;
  activeTopic: string | null;
  language: Language;
  currentPage: number;
  activeAuthorType: AuthorType;
};

type DeriveStateFromURLParams = {
  params: RouteParams;
  reportIDsWithTagV2IDs: ReportIDWithTagIDs[];
  tags: TagV2[];
  homeCategoryName: string;
};

function useDeriveStateFromURL({
  params,
  reportIDsWithTagV2IDs,
  tags,
  homeCategoryName,
}: DeriveStateFromURLParams): StateFromURL {
  const language = useDeriveLanguage();
  const currentPage = useDeriveCurrentPage();
  const activeAuthorType = useDeriveActiveAuthorType(tags);
  if (isReportPageParams(params)) {
    const activeReportID =
      parseReportParams(params.reportNameAndID).reportId ?? null;
    const tagPage = deriveTagPage({
      reportIDsWithTagV2IDs,
      tags,
      reportID: activeReportID,
    });
    return {
      page: tagPage ?? `blog`,
      activeTag: ``,
      activeReportID,
      activeRepo: null,
      activeTopic: null,
      language,
      currentPage,
      activeAuthorType,
    };
  }

  if (isRepoParams(params)) {
    return {
      page: `projects`,
      activeTag: ``,
      activeReportID: null,
      activeRepo: params.repoName,
      activeTopic: null,
      language,
      currentPage,
      activeAuthorType,
    };
  }

  if (isTopicParams(params)) {
    return {
      page: `topics`,
      activeTag: ``,
      activeReportID: null,
      activeRepo: null,
      activeTopic: params.topic,
      language,
      currentPage,
      activeAuthorType,
    };
  }

  const {page, tag} = params;
  return {
    page: derivePage({pageParam: page}),
    activeTag: deriveActiveTag({
      pageParam: page,
      tagParam: tag,
      tags,
      homeCategoryName,
    }),
    activeReportID: null,
    activeRepo: null,
    activeTopic: null,
    language,
    currentPage,
    activeAuthorType,
  };
}

type DerivePageParams = {pageParam: string | undefined};

function derivePage({pageParam}: DerivePageParams): FCPage {
  if (pageParam != null && isFCPage(pageParam)) {
    return pageParam;
  }
  return MAIN_PAGE;
}

type DeriveActiveTagParams = {
  pageParam: string | undefined;
  tagParam: string | undefined;
  tags: TagV2[];
  homeCategoryName: string;
};

function deriveActiveTag({
  pageParam,
  tagParam,
  tags,
  homeCategoryName,
}: DeriveActiveTagParams): string {
  let effectiveTagParam: string | undefined;
  if ((TAG_PAGES as string[]).includes(pageParam ?? '')) {
    effectiveTagParam = pageParam;
  } else if (tagParam != null) {
    effectiveTagParam = tagParam;
  } else if (isMountedInShell() && pageParam != null && !isFCPage(pageParam)) {
    effectiveTagParam = pageParam;
  }
  if (effectiveTagParam == null) {
    return homeCategoryName;
  }
  return formatGalleryTag(tags, effectiveTagParam);
}

function useDeriveLanguage(): Language {
  const location = useLocation();
  const {language} = qs.parse(location.search);
  if (
    language != null &&
    typeof language === 'string' &&
    isLanguage(language)
  ) {
    return language;
  }
  return DEFAULT_LANGUAGE;
}

function useDeriveCurrentPage(): number {
  const location = useLocation();
  const {page} = qs.parse(location.search);
  if (page != null && typeof page === 'string') {
    return Number(page);
  }
  return 1;
}

function useDeriveActiveAuthorType(tags: TagV2[]): AuthorType {
  const location = useLocation();
  const {author} = qs.parse(location.search);
  if (author != null && typeof author === 'string') {
    if (author === 'All') {
      return 'All';
    }
    const isAuthorTag = tags.some(
      t => t.name === author && t.navCategory === 'Authors'
    );
    if (isAuthorTag) {
      return author;
    }
    if (AUTHOR_TYPES.includes(author)) {
      return author;
    }
  }
  return 'All';
}

function useActiveReportParams(reportID: string | null): {
  loading: boolean;
  reportParams: ReportParams | null;
} {
  const {loading, reportMetadatas} = useReportMetadata(
    reportID != null ? [reportID] : []
  );

  const reportParams = useMemo(() => {
    if (reportMetadatas == null || reportMetadatas.length === 0) {
      return null;
    }
    const {id, entityName, project, displayName} = reportMetadatas[0];
    return {
      id,
      entityName: project?.entityName || entityName,
      projectName: project?.name,
      displayName,
    };
  }, [reportMetadatas]);

  return {loading, reportParams};
}

function useGitHubMetadata(repos: RepoWithUsage[]): RepoWithGitHubMetadata[] {
  const [reposWithGitHubMetadata, setReposWithGitHubMetadata] = useState<
    RepoWithGitHubMetadata[]
  >([]);

  useEffect(() => {
    (async () => {
      const metadatas = await Promise.all(repos.map(getGitHubMetadataForRepo));
      setReposWithGitHubMetadata(
        repos.map((r, i) => ({...r, ...metadatas[i]}))
      );
    })();
  }, [repos]);

  return reposWithGitHubMetadata;
}
